export type Reading = {
  title: string;
  href: string;
};

export type Session = {
  id: string;
  phase: string;
  weeks: string;
  title: string;
  focus: string;
  lab: string;
  output: string;
  hours: string;
  readings: Reading[];
};

export const phases = [
  { id: "0", label: "Prelude: Visual Circuits", sessions: "0.5", range: "Before W1", tone: "cyan" },
  { id: "I", label: "Foundations & Circuit Language", sessions: "1–5", range: "W1–10", tone: "blue" },
  { id: "II", label: "Superposition", sessions: "6–7", range: "W11–14", tone: "violet" },
  { id: "III", label: "SAEs as Instruments", sessions: "8–12", range: "W15–24", tone: "coral" },
  { id: "IV", label: "Diffing: What Training Changed", sessions: "13–16", range: "W25–32", tone: "gold" },
  { id: "V", label: "Attribution Graphs & Model Biology", sessions: "17–20", range: "W33–40", tone: "green" },
  { id: "VI", label: "Instruments & Audits", sessions: "21–26", range: "W41–52", tone: "navy" },
];

// session05Deck is served by this site itself (see Deck.tsx), hence an internal path.
export const resourceLinks = {
  session05Deck: "/session-0-5/deck",
  session1Deck: "/session-1/deck",
  session2Deck: "/session-2/deck",
  inceptionColab: "https://colab.research.google.com/drive/1hma00cfIupQ7Bwvwdc2e9zN5mBgTRVzM?usp=sharing",
  session1Colab: "https://colab.research.google.com/drive/1WrIn5t3NzAu0eKsvSyPJC7A4oPx7EurW?usp=sharing",
};

// Sessions and reading lists follow Plan/transformer-circuits-52week-plan-v2.md (2026-09).
// Session 0.5 and Phase I keep their v1 shape; from Phase III on the plan is rebuilt around
// instruments and audits. transformer-circuits.pub URLs were taken from the Thread's own index.
const TC = "https://transformer-circuits.pub";
const AF = "https://www.alignmentforum.org";

export const sessions: Session[] = [
  {
    id: "0.5", phase: "0", weeks: "Prelude",
    title: "Visual Circuits: Three Foundational Claims",
    focus: "The three claims — features, circuits, universality — plus equivariance and the first systematic record of polysemanticity.",
    lab: "Load InceptionV1 with Lucent, surface the curve-detector family with synthetic curve stimuli, and read the circuit off the weights.",
    output: "A one-page evidence audit: Features / Circuits / Universality. Its two closing questions come back in Sessions 12 and 18.",
    hours: "7h read · 4h code",
    readings: [
      { title: "Zoom In: An Introduction to Circuits", href: "https://distill.pub/2020/circuits/zoom-in/" },
      { title: "Curve Detectors", href: "https://distill.pub/2020/circuits/curve-detectors/" },
      { title: "Curve Circuits", href: "https://distill.pub/2020/circuits/curve-circuits/" },
      { title: "Multimodal Neurons", href: "https://distill.pub/2021/multimodal-neurons/" },
    ],
  },
  {
    id: "1", phase: "I", weeks: "W1–2",
    title: "Environment, GPT-2 from Scratch & Why We’re Here",
    focus: "The residual stream as a communication bus; shape conventions; einsum; microscope vs instrument — this year we build instruments; the logit lens as the simplest one.",
    lab: "Install the stack, implement GPT-2 small and match official weights layer by layer to <1e-4, add a logit lens. First AI trial run: hand the implementation to Claude Code, write the acceptance checks yourself, and audit what it got wrong.",
    output: "00_env.ipynb + 01_gpt2_from_scratch.ipynb + first instrument card (logit lens) + first “what the AI got wrong” log entry.",
    hours: "5h read · 8h lab",
    readings: [
      { title: "A Pragmatic Vision for Interpretability (Nanda et al.)", href: "https://www.lesswrong.com/posts/StENzDcD3kpfGJssR/a-pragmatic-vision-for-interpretability" },
      { title: "Getting Started in Mech Interp (Nanda)", href: "https://transformerlensorg.github.io/TransformerLens/content/getting_started_mech_interp.html" },
      { title: "TransformerLens Main Demo", href: "https://colab.research.google.com/github/TransformerLensOrg/TransformerLens/blob/main/demos/Main_Demo.ipynb" },
      { title: "Mech Interp Glossary (Nanda)", href: "https://www.neelnanda.io/mechanistic-interpretability/glossary" },
    ],
  },
  {
    id: "2", phase: "I", weeks: "W3–4",
    title: "Mathematical Framework I: Zero & One Layer",
    focus: "Path expansion; QK and OV circuits; bigrams and skip-trigrams; attention heads as low-rank bilinear operators.",
    lab: "Train a one-layer attention-only model, compute the bigram and QK matrices by hand, enumerate the top skip-trigrams, and verify the failure modes the paper points out.",
    output: "Skip-trigram table + QK/OV heatmaps.",
    hours: "6h read · 8h lab",
    readings: [
      { title: "A Mathematical Framework §1–3", href: `${TC}/2021/framework/index.html` },
      { title: "Framework Videos 1–2", href: `${TC}/2021/videos/index.html` },
    ],
  },
  {
    id: "3", phase: "I", weeks: "W5–6",
    title: "Mathematical Framework II: Two Layers & Composition",
    focus: "Q-/K-/V-composition; virtual heads; positive eigenvalues as a copying criterion; the analytic induction head in a two-layer model.",
    lab: "Compute composition-score matrices and OV-circuit eigenspectra on a two-layer model, mark the copying heads, and browse them with circuitsvis.",
    output: "Composition heatmap + eigenspectra + exercise solutions.",
    hours: "7h read · 9h lab",
    readings: [
      { title: "A Mathematical Framework §4–6", href: `${TC}/2021/framework/index.html` },
      { title: "Framework Exercises", href: `${TC}/2021/exercises/index.html` },
      { title: "Framework walkthrough (Nanda)", href: "https://www.neelnanda.io/mechanistic-interpretability/a-walkthrough-of-a-mathematical-framework-for-transformer-circuits" },
    ],
  },
  {
    id: "4", phase: "I", weeks: "W7–8",
    title: "Induction Heads I: Phase Change & In-Context Learning",
    focus: "In-context learning score; the phase change during training; prefix matching and copying as twin criteria; the induction detector as our first behavior detector.",
    lab: "Score all 144 GPT-2 small heads with repeated random tokens and reproduce the per-token loss curves.",
    output: "Ranked head table + induction attention visualization + instrument card (induction detector).",
    hours: "5h read · 9h lab",
    readings: [
      { title: "Induction Heads · Arguments 1–3", href: `${TC}/2022/in-context-learning-and-induction-heads/index.html` },
    ],
  },
  {
    id: "5", phase: "I", weeks: "W9–10",
    title: "Induction Heads II, IOI & Instrument Zero: the Linear Probe",
    focus: "Ablation, activation patching and path patching as causal evidence; probes see correlation, patching sees causation; the three probe traps — the probe learns the task, presence is not use, distribution shift.",
    lab: "Reproduce the IOI circuit with path patching, train per-layer linear probes on the same task and compare the accuracy curve with the patched layers, then ablate the induction heads.",
    output: "IOI circuit diagram + probe-vs-patching plot + two instrument cards (activation patching, linear probe). Phase I milestone.",
    hours: "6h read · 10h lab",
    readings: [
      { title: "Induction Heads · Arguments 4–6", href: `${TC}/2022/in-context-learning-and-induction-heads/index.html` },
      { title: "Revisiting the Framework (Jul 2025 update)", href: `${TC}/2025/july-update/index.html` },
      { title: "Linear Classifier Probes (Alain & Bengio) §1–3", href: "https://arxiv.org/abs/1610.01644" },
      { title: "Probing Classifiers: Promises, Shortcomings, Advances (Belinkov)", href: "https://arxiv.org/abs/2102.12452" },
      { title: "The Geometry of Truth (Marks & Tegmark) §1–3", href: "https://arxiv.org/abs/2310.06824" },
    ],
  },
  {
    id: "6", phase: "II", weeks: "W11–12",
    title: "Toy Models of Superposition I",
    focus: "Features vs directions vs bases; sparsity and importance; phase diagrams.",
    lab: "Run the official Colab, then rebuild the toy-model training loop and sweep sparsity and importance.",
    output: "Reproduced phase-diagram notebook.",
    hours: "5h read · 8h lab",
    readings: [
      { title: "Toy Models of Superposition §1–4", href: `${TC}/2022/toy_model/index.html` },
    ],
  },
  {
    id: "7", phase: "II", weeks: "W13–14",
    title: "Toy Models II + Double Descent & Privileged Bases",
    focus: "Fractional dimensionality; computation in superposition; the three regimes; why Adam gives the residual stream a privileged basis; and what it means for instruments — neuron-level monitoring is unreliable by construction.",
    lab: "Plot the dimensionality histogram and the fractional-dimension staircase, run the computation-in-superposition task, and hand the optional double-descent replication to the AI.",
    output: "Staircase plot + one page on why dictionary learning is needed. Phase II milestone.",
    hours: "6h read · 8h lab",
    readings: [
      { title: "Toy Models of Superposition §5–8", href: `${TC}/2022/toy_model/index.html` },
      { title: "Superposition, Memorization & Double Descent (§1 + conclusion)", href: `${TC}/2023/toy-double-descent/index.html` },
      { title: "Privileged Bases in the Residual Stream (conclusion)", href: `${TC}/2023/privileged-basis/index.html` },
      { title: "Softmax Linear Units (optional)", href: `${TC}/2022/solu/index.html` },
    ],
  },
  {
    id: "8", phase: "III", weeks: "W15–16",
    title: "Towards Monosemanticity: Train Your Own SAE",
    focus: "The SAE objective; expansion factor; dead features; the L0–reconstruction Pareto; feature splitting; cross-seed universality; autointerp scoring.",
    lab: "Train a 4096-feature SAE on a one-layer transformer, build feature dashboards, and score features with the Claude API — the first time AI explains AI.",
    output: "Your own SAE + dashboards + autointerp score table + instrument card (SAE feature as observer).",
    hours: "6h read · 10h lab",
    readings: [
      { title: "Towards Monosemanticity §1–6", href: `${TC}/2023/monosemantic-features/index.html` },
      { title: "Attacking Superposition (May 2023 update)", href: `${TC}/2023/may-update/index.html` },
    ],
  },
  {
    id: "9", phase: "III", weeks: "W17–18",
    title: "SAE Engineering & Architectures in One Pass",
    focus: "Dead-feature remedies; L1 shrinkage bias; Gated, TopK and JumpReLU; scaling laws for dictionary learning. Read until you know what each knob does and when to turn it — no derivations.",
    lab: "No hand-written architectures: train SAELens’ standard, TopK and JumpReLU SAEs on the same activations, plot one Pareto chart, measure shrinkage; a small scaling law is run by the AI.",
    output: "Architecture comparison plot + one-page SAE training knob cheat sheet.",
    hours: "7h read · 6h lab",
    readings: [
      { title: "Circuits Updates — Feb 2024", href: `${TC}/2024/feb-update/index.html` },
      { title: "Circuits Updates — Mar 2024", href: `${TC}/2024/march-update/index.html` },
      { title: "Circuits Updates — Apr 2024", href: `${TC}/2024/april-update/index.html` },
      { title: "TopK vs Gated SAEs (Jun 2024 update)", href: `${TC}/2024/june-update/index.html` },
      { title: "Gated SAEs §1–3", href: "https://arxiv.org/abs/2404.16014" },
      { title: "TopK SAEs §1–3", href: "https://arxiv.org/abs/2406.04093" },
      { title: "JumpReLU SAEs §1–3", href: "https://arxiv.org/abs/2407.14435" },
      { title: "Circuits Updates — Jan 2025", href: `${TC}/2025/january-update/index.html` },
      { title: "Data-Point Initialization (Oct 2025 update)", href: `${TC}/2025/october-update/index.html` },
    ],
  },
  {
    id: "10", phase: "III", weeks: "W19–20",
    title: "Scaling Monosemanticity I + Pretrained SAEs",
    focus: "From toys to production models; abstract features; attribution and ablation to test whether a feature takes part in computation; pretrained SAEs as the default source of instruments.",
    lab: "Load Gemma-2-2B with Gemma Scope, browse latents on Neuronpedia, find the top features for a prompt and ablate them. Decision point: try Gemma 3 4B + Gemma Scope 2 and decide whether to switch.",
    output: "Gemma Scope analysis notebook + model choice recorded in the claims ledger.",
    hours: "6h read · 9h lab",
    readings: [
      { title: "Scaling Monosemanticity §1–3", href: `${TC}/2024/scaling-monosemanticity/index.html` },
      { title: "Gemma Scope technical report §1–2", href: "https://arxiv.org/abs/2408.05147" },
      { title: "Announcing Gemma Scope 2", href: `${AF}/posts/YQro5LyYjDzZrBCdb/announcing-gemma-scope-2` },
    ],
  },
  {
    id: "11", phase: "III", weeks: "W21–22",
    title: "Scaling Monosemanticity II: Safety Features, Steering, Classifier vs Probe",
    focus: "Safety-relevant features; feature steering and clamp strength; precision and recall of features as classifiers; the first head-to-head — SAE feature classifier vs linear probe.",
    lab: "Sweep steering strength; build classifiers from two safety features and a linear probe on the same data, plot both ROCs, then shift the distribution and see which one drops first.",
    output: "Steering demo + classifier/probe ROC + two instrument cards (feature steering, feature classifier). Mid-year milestone.",
    hours: "6h read · 10h lab",
    readings: [
      { title: "Scaling Monosemanticity §4–6", href: `${TC}/2024/scaling-monosemanticity/index.html` },
      { title: "Dictionary Features as Classifiers", href: `${TC}/2024/features-as-classifiers/index.html` },
    ],
  },
  {
    id: "12", phase: "III", weeks: "W23–24",
    title: "The Limits of SAEs as Instruments",
    focus: "Dark matter; feature sensitivity; the edge of the linear-representation hypothesis; evals that don’t fool themselves; the evidence that probes beat SAEs out of distribution, and where SAEs still earn their place.",
    lab: "Quantify dark matter and sensitivity in your SAE, extend the probe-vs-SAE comparison to more distributions, and write the eval-harness skeleton the rest of the year reuses.",
    output: "SAE instrument-limits report + eval harness v0. Phase III milestone: know when to use a probe, an SAE, or neither.",
    hours: "7h read · 8h lab",
    readings: [
      { title: "Circuits Updates — Jul 2024", href: `${TC}/2024/july-update/index.html` },
      { title: "Circuits Updates — Aug 2024", href: `${TC}/2024/august-update/index.html` },
      { title: "Circuits Updates — Sep 2024", href: `${TC}/2024/september-update/index.html` },
      { title: "Circuits Updates — May 2026", href: `${TC}/2026/may-update/index.html` },
      { title: "Circuits Updates — Jun 2026", href: `${TC}/2026/june-update/index.html` },
      { title: "Negative Results for SAEs on Downstream Tasks (DeepMind)", href: `${AF}/posts/4uXCAJNuPKtKBsi28/negative-results-for-saes-on-downstream-tasks` },
      { title: "Are Sparse Autoencoders Useful? (Kantamneni et al.)", href: "https://arxiv.org/abs/2502.16681" },
    ],
  },
  {
    id: "13", phase: "IV", weeks: "W25–26",
    title: "Sparse Crosscoders",
    focus: "A dictionary that reads and writes across layers or models; cross-layer superposition; acausal vs strictly causal variants; why diffing needs it.",
    lab: "Train a crosscoder spanning three to four layers of Gemma-2-2B and plot the cross-layer distribution of its features.",
    output: "Crosscoder + cross-layer distribution plot.",
    hours: "5h read · 10h lab",
    readings: [
      { title: "Sparse Crosscoders", href: `${TC}/2024/crosscoders/index.html` },
    ],
  },
  {
    id: "14", phase: "IV", weeks: "W27–28",
    title: "Model Diffing I: Base vs Instruct & Persona Vectors",
    focus: "Feature differences between base and fine-tuned models; complete-shrinkage artifacts; stage-wise diffing; persona vectors and their three uses — monitoring, steering, preventative steering.",
    lab: "Diff gemma-2-2b against its instruct model and audit each instruct-only feature; extract a persona vector for one trait, confirm it steers, then use it to watch drift during a small LoRA fine-tune.",
    output: "Diff feature list + persona-vector monitoring curve + two instrument cards (crosscoder diffing, persona vector).",
    hours: "6h read · 10h lab",
    readings: [
      { title: "Stage-Wise Model Diffing", href: `${TC}/2024/model-diffing/index.html` },
      { title: "Insights on Crosscoder Model Diffing", href: `${TC}/2025/crosscoder-diffing-update/index.html` },
      { title: "Persona diffing (Aug 2025 update)", href: `${TC}/2025/august-update/index.html` },
      { title: "Persona Vectors", href: "https://arxiv.org/abs/2507.21509" },
    ],
  },
  {
    id: "15", phase: "IV", weeks: "W29–30",
    title: "Model Diffing II: What Post-Training and RL Actually Change",
    focus: "H1: RL mostly amplifies existing features rather than adding new ones. H2: the features behind verification and backtracking already exist in the base model. Narrow fine-tuning generalising to broad misalignment is the same amplifier at work.",
    lab: "Train a diffing crosscoder on the RL-only pair (R1-Distill-Qwen-1.5B → DeepScaleR-1.5B), attribute “Wait / Let me verify” tokens to features, check whether they exist in the base, and steer them there to test H2.",
    output: "One-page “what RL changed” report with H1/H2 supported or rejected + claims ledger update.",
    hours: "5h read · 11h lab",
    readings: [
      { title: "Emergent Misalignment (Betley et al.)", href: "https://arxiv.org/abs/2502.17424" },
      { title: "Toward understanding and preventing misalignment generalization (OpenAI)", href: "https://openai.com/index/emergent-misalignment/" },
      { title: "Does RL Really Incentivize Reasoning Beyond the Base Model? (Yue et al.)", href: "https://arxiv.org/abs/2504.13837" },
      { title: "Cognitive Behaviors that Enable Self-Improving Reasoners (Gandhi et al.)", href: "https://arxiv.org/abs/2503.01307" },
      { title: "DeepScaleR: Scaling RL on a 1.5B model", href: "https://pretty-radio-b75.notion.site/DeepScaleR-Surpassing-O1-Preview-with-a-1-5B-Model-by-Scaling-RL-19681902c1468005bed8ca303013a4e2" },
    ],
  },
  {
    id: "16", phase: "IV", weeks: "W31–32",
    title: "Transcoders & MOLT: Parts for Attribution Graphs",
    focus: "A transcoder learns the MLP’s input-to-output map instead of reconstructing activations; why attribution graphs need it; MOLT as an interpretable MLP approximation.",
    lab: "Load Gemma Scope’s gemma-2-2b transcoders and measure the KL after replacing each layer; train a small transcoder of your own as a control.",
    output: "Replacement-fidelity table. Phase IV milestone: the parts for an attribution graph are ready.",
    hours: "5h read · 9h lab",
    readings: [
      { title: "Transcoders Find Interpretable LLM Feature Circuits (Dunefsky et al.) §1–3", href: "https://arxiv.org/abs/2406.11944" },
      { title: "Sparse Mixtures of Linear Transforms", href: `${TC}/2025/bulk-update/index.html` },
      { title: "A Toy Model of Interference Weights (optional)", href: `${TC}/2025/interference-weights/index.html` },
    ],
  },
  {
    id: "17", phase: "V", weeks: "W33–34",
    title: "Circuit Tracing I: Method",
    focus: "Cross-layer transcoders; local replacement models; the nodes (feature, embedding, error, logit) and edges of attribution graphs; pruning.",
    lab: "Install circuit-tracer, run the Gemma-2-2B demo end to end, and inspect your first attribution graph in the Neuronpedia front end.",
    output: "Your first attribution graph + instrument card (attribution graph).",
    hours: "6h read · 9h lab",
    readings: [
      { title: "Circuit Tracing §1–4", href: `${TC}/2025/attribution-graphs/methods.html` },
      { title: "circuit-tracer (safety-research)", href: "https://github.com/safety-research/circuit-tracer" },
    ],
  },
  {
    id: "18", phase: "V", weeks: "W35–36",
    title: "Circuit Tracing II: Validation & Limits",
    focus: "Feature interventions to test the graph; error-node share as an incompleteness measure; frozen attention as a blind spot; mechanistic unfaithfulness. Answering Session 0.5’s second question: on transformers, “reimplement it” gives way to “interventions change behavior as predicted”.",
    lab: "Run three intervention experiments on the Session 17 graph, quantify how well predictions match measurements, and tally error-node weight across prompts.",
    output: "Intervention validation table + limitations notes.",
    hours: "6h read · 9h lab",
    readings: [
      { title: "Circuit Tracing §5–7", href: `${TC}/2025/attribution-graphs/methods.html` },
      { title: "A Toy Model of Mechanistic (Un)Faithfulness", href: `${TC}/2025/faithfulness-toy-model/index.html` },
      { title: "Fast Circuit Identification (Mar 2024 update)", href: `${TC}/2024/march-update/index.html` },
    ],
  },
  {
    id: "19", phase: "V", weeks: "W37–38",
    title: "Biology of an LLM I: Reasoning, Planning, Multilinguality",
    focus: "Intermediate concept nodes in multi-step reasoning; forward planning in poetry; language-agnostic abstract representations; the addition mechanism.",
    lab: "Reproduce the two-hop QA graph, the addition circuit, and the rhyme-planning interventions on Gemma-2-2B.",
    output: "Three annotated attribution graphs.",
    hours: "7h read · 9h lab",
    readings: [
      { title: "Biology of an LLM §1–5", href: `${TC}/2025/attribution-graphs/biology.html` },
    ],
  },
  {
    id: "20", phase: "V", weeks: "W39–40",
    title: "Biology of an LLM II: Refusals, Jailbreaks, Hallucination, Hidden Goals",
    focus: "Known-entity features suppress the default refusal — the origin of hallucination; how jailbreaks slip past safety features and when the model catches itself; what a hidden goal looks like on an attribution graph.",
    lab: "Reproduce the hallucination circuit and one jailbreak graph; if the Session 23 model organism is ready, graph its hidden behavior early.",
    output: "A casebook of safety-relevant circuits. Phase V milestone: run a model-biology investigation on your own.",
    hours: "7h read · 9h lab",
    readings: [
      { title: "Biology of an LLM §6–11", href: `${TC}/2025/attribution-graphs/biology.html` },
      { title: "Circuits Updates — Apr 2025", href: `${TC}/2025/april-update/index.html` },
      { title: "Harm pressure (Nov 2025 update)", href: `${TC}/2025/november-update/index.html` },
    ],
  },
  {
    id: "21", phase: "VI", weeks: "W41–42",
    title: "Chain-of-Thought Faithfulness & Monitorability",
    focus: "Faithfulness is not monitorability; hint protocols; the three ways CoT monitoring breaks — RL pressure, architecture change, direct optimization against the monitor. CoT is a product of RL: an instrument that training can distort, not ground truth.",
    lab: "On Qwen3-4B in thinking mode, build three hint types and measure accuracy shift vs mention rate; patch hint tokens with nnsight (answer changes, CoT doesn’t = unverbalized dependence); train a hint-use probe; test a small-LLM CoT monitor.",
    output: "Faithfulness table + casebook of unverbalized dependencies + instrument card (CoT monitor, with failure conditions).",
    hours: "7h read · 9h lab",
    readings: [
      { title: "Reasoning Models Don’t Always Say What They Think", href: "https://www.anthropic.com/research/reasoning-models-dont-say-think" },
      { title: "Chain of Thought Monitorability (Korbak et al.)", href: "https://arxiv.org/abs/2507.11473" },
      { title: "Monitoring Reasoning Models for Misbehavior (OpenAI)", href: "https://openai.com/index/chain-of-thought-monitoring/" },
      { title: "Unfaithful Explanations in CoT (Turpin et al.)", href: "https://arxiv.org/abs/2305.04388" },
      { title: "Alignment Faking (optional)", href: "https://arxiv.org/abs/2412.14093" },
    ],
  },
  {
    id: "22", phase: "VI", weeks: "W43–44",
    title: "Activation Monitoring in Deployment",
    focus: "Why simple defection probes work; choosing what to monitor; precision and recall decay and calibration under shift; oracles that read activations; monitoring long-horizon agent runs.",
    lab: "Build a contrastive dataset for one latent state on Qwen3-4B, train a probe, test it on three OOD distributions against the Session 12 SAE baseline, and attach it to every step of a multi-step agent task.",
    output: "Monitor + OOD decay curve + calibration plot + instrument card (activation probe monitor).",
    hours: "6h read · 10h lab",
    readings: [
      { title: "Simple probes can catch sleeper agents", href: "https://www.anthropic.com/research/probes-catch-sleeper-agents" },
      { title: "Sleeper Agents (§1 + conclusion)", href: "https://arxiv.org/abs/2401.05566" },
      { title: "Activation Oracles", href: "https://alignment.anthropic.com/2025/activation-oracles/" },
      { title: "Frontier Models are Capable of In-context Scheming (Apollo)", href: "https://arxiv.org/abs/2412.04984" },
    ],
  },
  {
    id: "23", phase: "VI", weeks: "W45–46",
    title: "Automated Interpretability: from Autointerp to Interp Agents",
    focus: "The explain → predict → score loop; natural-language autoencoders; tool interfaces for an interp agent; evaluating agents by planting a known quirk.",
    lab: "Implant a quirk in Qwen3-1.7B with LoRA, wrap nnsight, SAE lookup and steering as tools for Claude Code, and let it hunt. You write the brief and acceptance criteria and audit its trajectory.",
    output: "Interp agent v0 + an “AI auditing AI” review log + instrument card (interp agent).",
    hours: "5h read · 11h lab",
    readings: [
      { title: "Language models can explain neurons in language models (Bills et al.)", href: "https://openai.com/index/language-models-can-explain-neurons-in-language-models/" },
      { title: "Self-explaining features (Aug 2024 update)", href: `${TC}/2024/august-update/index.html` },
      { title: "Natural Language Autoencoders", href: `${TC}/2026/nla/index.html` },
      { title: "Building and evaluating alignment auditing agents", href: "https://alignment.anthropic.com/2025/automated-auditing/" },
      { title: "Investigator Agents (Transluce)", href: "https://transluce.org/automated-elicitation" },
    ],
  },
  {
    id: "24", phase: "VI", weeks: "W47–48",
    title: "AI Auditing",
    focus: "The audit evidence pyramid — behavior, training data, activations, circuits; blind-audit design; black-box and white-box as complements; stating confidence in an audit report.",
    lab: "A small auditing game: team A implants a hidden objective, team B gets six hours and the full instrument kit to find it, then the teams swap; run Petri as the automated control.",
    output: "Two audit reports + a debrief on which instruments actually found something and which only looked like they were working.",
    hours: "6h read · 10h lab",
    readings: [
      { title: "Auditing Language Models for Hidden Objectives", href: "https://www.anthropic.com/research/auditing-hidden-objectives" },
      { title: "Building and evaluating alignment auditing agents", href: "https://alignment.anthropic.com/2025/automated-auditing/" },
      { title: "Petri: an open-source auditing tool", href: "https://alignment.anthropic.com/2025/petri/" },
      { title: "Claude Fable 5.1 & Mythos 5.1 system card (alignment chapter)", href: "https://www-cdn.anthropic.com/0339e6a7c5c7b87f5c07798616dc32c215d14235/Claude%20Fable%205.1%20&%20Claude%20Mythos%205.1%20System%20Card.pdf" },
    ],
  },
  {
    id: "25", phase: "VI", weeks: "W49–50",
    title: "Introspection: a Fragile Instrument",
    focus: "The concept-injection protocol; models reporting their own internal states — roughly 20% detection even under the best protocol, zero false positives, highly prompt-sensitive. Introspection as low-recall cross-validation, never sole evidence.",
    lab: "Implement thought injection with nnsight on Llama-3.1-8B or Qwen3-8B, sweep injection layer and strength, measure the success rate and reproduce the prompt sensitivity.",
    output: "Replication + difference analysis + instrument card (introspection).",
    hours: "6h read · 9h lab",
    readings: [
      { title: "Emergent Introspective Awareness", href: `${TC}/2025/introspection/index.html` },
      { title: "Circuits Updates — Sep 2025", href: `${TC}/2025/september-update/index.html` },
      { title: "Open replication (arXiv:2512.12411)", href: "https://arxiv.org/abs/2512.12411" },
    ],
  },
  {
    id: "26", phase: "VI", weeks: "W51–52",
    title: "Capstone: the Audit Harness",
    focus: "Run the full six-layer instrument stack on one small model — behavioral evals, CoT monitoring, activation monitoring, diffing against base, attribution graphs for key behaviors, agent audit — as a scaled-down version of a frontier pre-release audit.",
    lab: "Assemble the pipeline on Qwen3-4B or Gemma 4 E4B, reusing the instruments from Sessions 12–24, and ship it as a reusable repo.",
    output: "Repo + audit report with stated confidence + Instrument Handbook v1 + 3–5 open questions.",
    hours: "3h read · 14h lab",
    readings: [
      { title: "200 Concrete Open Problems in Mech Interp (Nanda)", href: `${AF}/s/yivyHaCAmMJ3CqSyj` },
    ],
  },
];

export const PROGRAM_KICKOFF = Date.UTC(2026, 7, 30);
export const PROGRAM_WEEKS = 54;

export function sessionRange(id: string) {
  const start = id === "0.5" ? Date.UTC(2026, 7, 30) : Date.UTC(2026, 8, 13 + (Number(id) - 1) * 14);
  return { start, end: start + 14 * 86400000 };
}

export function sessionDates(id: string) {
  const range = sessionRange(id);
  const start = new Date(range.start);
  const end = new Date(range.end);
  const pad = (value: number) => String(value).padStart(2, "0");
  const startText = `${start.getUTCFullYear()}.${pad(start.getUTCMonth() + 1)}.${pad(start.getUTCDate())}`;
  // Within the same year, only the start date carries the year, to keep this column short.
  const endText = end.getUTCFullYear() === start.getUTCFullYear()
    ? `${pad(end.getUTCMonth() + 1)}.${pad(end.getUTCDate())}`
    : `${end.getUTCFullYear()}.${pad(end.getUTCMonth() + 1)}.${pad(end.getUTCDate())}`;
  return `${startText} – ${endText}`;
}
